/**
 * Spend trend chart for the Overview page — the representative Recharts chart
 * for Wave 2A's `chartTheme`. Grouped/stacked BarChart of fleet spend over the
 * selected period, wrapped in the fleetGUARDIAN `<Section>` panel with an in-
 * card `<VehicleDimensionSelect />`.
 *
 * The axis/grid/tooltip/animation styling comes from `@/lib/chartTheme` so it
 * matches the fleetGUARDIAN DevExpress look; series colours use CHART_PALETTE.
 * X-axis tick labels are pre-formatted by the backend (`bucket_label`); the
 * Y-axis is ZAR with k/M abbreviation (`fmtRShort`).
 */

import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { Section } from '@/components/fleetguardian';
import { fmtR, fmtRShort } from '@/components/fleetguardian/format';
import {
  CHART_ANIMATION_MS,
  CHART_PALETTE,
  chartAxis,
  chartGrid,
  chartLegend,
  chartTooltipItemStyle,
  chartTooltipLabelStyle,
  chartTooltipStyle,
} from '@/lib/chartTheme';
import { VehicleDimensionSelect } from '@/components/shared/VehicleDimensionSelect';
import type { SpendTrendResponse } from '@/types/overview';

export interface SpendTrendChartProps {
  data: SpendTrendResponse | undefined;
  isLoading: boolean;
  error: Error | null;
}

export function SpendTrendChart({
  data,
  isLoading,
  error,
}: SpendTrendChartProps): JSX.Element {
  // Pivot the wire shape (one row per (bucket, series)) into the wide shape
  // Recharts wants (one row per bucket, with one column per series).
  const { rows, seriesLabels } = pivot(data);
  const dimSuffix =
    data?.dim && data.dim !== 'all' ? ` — by ${humaniseDim(data.dim)}` : '';

  return (
    <Section
      title={`Fleet Spend over Period${dimSuffix}`}
      actions={<VehicleDimensionSelect />}
    >
      <div className="h-[320px]">
        {error ? (
          <div className="flex h-full items-center justify-center text-sm text-destructive">
            Failed to load spend trend: {error.message}
          </div>
        ) : isLoading || !data ? (
          <Skeleton className="h-full w-full" />
        ) : rows.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            No spend recorded in this period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 8, bottom: 4, left: 4 }}>
              <CartesianGrid {...chartGrid} />
              <XAxis dataKey="bucket_label" {...chartAxis} />
              <YAxis {...chartAxis} tickFormatter={fmtRShort} width={64} />
              <Tooltip
                cursor={{ fill: 'rgba(31,107,117,0.06)' }}
                formatter={(v: number) => fmtR(v)}
                labelFormatter={(l) => `Bucket: ${l}`}
                contentStyle={chartTooltipStyle}
                itemStyle={chartTooltipItemStyle}
                labelStyle={chartTooltipLabelStyle}
              />
              {seriesLabels.length > 1 && <Legend {...chartLegend} />}
              {seriesLabels.map((label, i) => (
                <Bar
                  key={label}
                  dataKey={label}
                  stackId={data.dim === 'all' ? undefined : 'spend'}
                  fill={CHART_PALETTE[i % CHART_PALETTE.length]}
                  name={label === 'all' ? 'All vehicles' : label}
                  radius={data.dim === 'all' ? [4, 4, 0, 0] : undefined}
                  animationDuration={CHART_ANIMATION_MS}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </Section>
  );
}

function humaniseDim(dim: string): string {
  switch (dim) {
    case 'make':
      return 'make';
    case 'make_model':
      return 'make & model';
    case 'make_model_year':
      return 'make / model / year';
    default:
      return dim;
  }
}

interface PivotedRow {
  bucket_label: string;
  bucket_start: string;
  [seriesLabel: string]: string | number;
}

// One <Bar> is rendered per series label. Grouping by make_model over a long
// period yields ~3,940 distinct labels, which is 3,940 Bar components plus a
// 3,940-entry legend — enough to lock the browser tab with no error state, on
// a chart nobody could read anyway. Cap the series and roll the tail into
// "Other" so the total stays truthful.
const MAX_SERIES = 10;
const OTHER_LABEL = 'Other';

function pivot(
  data: SpendTrendResponse | undefined,
): { rows: PivotedRow[]; seriesLabels: string[] } {
  if (!data) return { rows: [], seriesLabels: [] };

  // Rank labels by total spend across all buckets, not by first appearance,
  // so the bars kept are the ones that actually matter.
  const totals = new Map<string, number>();
  for (const b of data.buckets) {
    for (const s of b.series) {
      totals.set(s.label, (totals.get(s.label) ?? 0) + s.spend);
    }
  }

  const ranked = Array.from(totals.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([label]) => label);

  const kept = new Set(ranked.slice(0, MAX_SERIES));
  const hasOther = ranked.length > MAX_SERIES;
  const seriesLabels = hasOther
    ? [...ranked.slice(0, MAX_SERIES), OTHER_LABEL]
    : ranked;

  const rows: PivotedRow[] = data.buckets.map((b) => {
    const row: PivotedRow = {
      bucket_label: b.bucket_label,
      bucket_start: b.bucket_start,
    };
    // Initialise every series to 0 so missing buckets don't break the stack.
    for (const label of seriesLabels) row[label] = 0;
    for (const s of b.series) {
      if (kept.has(s.label)) {
        row[s.label] = s.spend;
      } else {
        row[OTHER_LABEL] = (row[OTHER_LABEL] as number) + s.spend;
      }
    }
    return row;
  });

  return { rows, seriesLabels };
}

export default SpendTrendChart;
